import React, { useState, useEffect } from 'react'
import { Card, Space, Typography, Alert, Button, theme, Select, Tooltip, Spin, Input } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileText, faRecycle } from '@fortawesome/free-solid-svg-icons'
import TypingAnimation from './TypingAnimation'
import TranscribeActionButtons from './TranscribeActionButtons'
import { getStructureOptions } from '../utils/structureOptions'

const { Text } = Typography
const { TextArea } = Input

const ContentDisplay = ({
  isProcessing,
  isTyping,
  generatedNote,
  rawAiResponse,
  error,
  onRetry,
  onTypingComplete,
  handleRegenerate,
  onContentChange,
  onInsertToNote,
  onClearWorkspace,
  onDataExtraction,
  isExtracting,
  bookingData,
  userRef
}) => {
  const { token } = theme.useToken()
  const [selectedStructure, setSelectedStructure] = useState('SOAP')
  const [canRegenerate, setCanRegenerate] = useState(false) // Track if regeneration is available

  // Reset regenerate availability when a new note is generated
  useEffect(() => {
    if (generatedNote?.trim()) {
      setCanRegenerate(true)
    } else {
      setCanRegenerate(false)
    }
  }, [generatedNote])

  const handleStructureChange = (value) => {
    setSelectedStructure(value)
    // Enable regenerate button when structure is changed and note exists
    if (generatedNote?.trim()) {
      setCanRegenerate(true)
    }
  }

  const handleRegenerateClick = async () => {
    if (!handleRegenerate || !selectedStructure) return

    setCanRegenerate(false) // Disable button during regeneration
    await handleRegenerate(selectedStructure)
  }

  // Helper function to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return ''
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  const handleContentEdit = (e) => {
    if (onContentChange) {
      onContentChange(e.target.value)
    }
  }

  const renderContent = () => {
    if (isProcessing) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '350px',
            textAlign: 'center',
            padding: '16px',
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          <Spin size='large' style={{ marginBottom: '24px' }} />
          <Text strong style={{ fontSize: '16px', marginBottom: '8px' }}>
            Generating your note...
          </Text>
          <Text type='secondary' style={{ fontSize: '14px' }}>
            Please don't leave this page until the note is generated
          </Text>
        </div>
      )
    }

    if (isTyping) {
      return (
        <div
          style={{
            padding: '16px',
            fontSize: '18px',
            lineHeight: '1.6',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          <TypingAnimation text={rawAiResponse} speed={3} onComplete={onTypingComplete} />
        </div>
      )
    }

    if (generatedNote) {
      return (
        <>
          {/* Action Buttons */}
          <TranscribeActionButtons
            content={stripHtml(generatedNote)}
            onInsertToNote={onInsertToNote}
            onClearWorkspace={onClearWorkspace}
            onDataExtraction={onDataExtraction}
            isExtracting={isExtracting}
            bookingData={bookingData}
            userRef={userRef}
          />

          {/* Generated Content */}
          <div style={{ flex: 1, overflow: 'visible', padding: '16px' }}>
            <TextArea
              value={stripHtml(generatedNote)}
              onChange={handleContentEdit}
              autoSize={true}
              style={{
                fontSize: '18px',
                lineHeight: '1.6',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                border: 'none',
                padding: 0,
                boxShadow: 'none',
                resize: 'none',
                background: 'transparent',
                width: '100%'
              }}
            />
          </div>
        </>
      )
    }

    if (error) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '350px',
            textAlign: 'center',
            padding: '16px'
          }}
        >
          <Alert
            message='Processing Failed'
            description={error}
            type='error'
            showIcon
            style={{ marginBottom: '16px' }}
          />
          <Button type='primary' onClick={onRetry} className='transcribe-button'>
            Try Again
          </Button>
        </div>
      )
    }

    // Empty state with clear instructions
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '350px',
          textAlign: 'center',
          color: token.colorText,
          padding: '24px',
          maxWidth: '400px',
          margin: '0 auto'
        }}
      >
        <Text style={{ fontSize: '18px', fontWeight: '600', color: token.colorText }}>
          Welcome to the future of health care
        </Text>
      </div>
    )
  }

  const getWordCount = () => {
    if (generatedNote || isTyping) {
      return (generatedNote || rawAiResponse).replace(/<[^>]*>/g, '').split(' ').length
    }
    return null
  }

  const wordCount = getWordCount()
  const structureOptions = getStructureOptions()

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          {/* Left side - title and word count */}
          <Space>
            <FontAwesomeIcon icon={faFileText} style={{ color: token.colorSuccess }} />
            <span style={{ fontSize: '24px', fontWeight: '600', color: token.colorPrimary }}>
              Generated Clinical Note
            </span>
            {wordCount && (
              <Text type='secondary' style={{ fontSize: '12px' }}>
                ({wordCount} words)
              </Text>
            )}
          </Space>

          {/* Right side - Structure dropdown and buttons */}
          <Space size={8} style={{ marginLeft: '16px', flexWrap: 'wrap' }}>
            <Select
              value={selectedStructure}
              onChange={handleStructureChange}
              disabled={isProcessing || isTyping}
              size='large'
              style={{
                minWidth: '180px',
                fontSize: '14px'
              }}
              options={structureOptions}
              placeholder='Structure'
            />

            <Tooltip title='Regenerate note using selected structure' placement='bottom'>
              <Button
                disabled={isProcessing || isTyping || !canRegenerate}
                style={{
                  backgroundColor: canRegenerate && !isProcessing && !isTyping ? '#1890ff' : '#f5f5f5',
                  borderColor: canRegenerate && !isProcessing && !isTyping ? '#1890ff' : '#d9d9d9',
                  color: canRegenerate && !isProcessing && !isTyping ? 'white' : '#666666',
                  borderRadius: '4px',
                  fontSize: '14px',
                  padding: '0 12px',
                  transition: 'all 0.2s ease'
                }}
                onClick={handleRegenerateClick}
                type='default'
                size='large'
              >
                <FontAwesomeIcon
                  icon={faRecycle}
                  style={{
                    fontSize: '14px',
                    marginRight: '6px',
                    color: canRegenerate && !isProcessing && !isTyping ? 'white' : '#666666'
                  }}
                />
                Regenerate
              </Button>
            </Tooltip>
          </Space>
        </div>
      }
      className='ai-processing-card'
      style={{
        height: 'calc(100vh - 150px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}
      styles={{
        header: {
          backgroundColor: '#f0f7ff',
          borderBottom: `1px solid ${token.colorPrimary}20`
        }
      }}
      bodyStyle={{
        flex: 1,
        overflow: 'auto',
        padding: 0,
        height: 'calc(100vh - 210px)'
      }}
    >
      <div style={{ height: '100%', overflow: 'auto' }}>{renderContent()}</div>
    </Card>
  )
}

export default ContentDisplay
